// @ts-nocheck

import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EventFormData } from "../CreateEventForm";
import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import EventGalleryUpload from "./EventGalleryUpload";
import BackgroundImageUpload from "./BackgroundImageUpload";
import { BasicRichTextEditor } from "@/components/editor/BasicRichTextEditor";

interface DetailsTabProps {
  formData: EventFormData;
  updateFormData: (data: Partial<EventFormData>) => void;
}

interface EventRegistrationForm {
  id: string;
  name: string;
}

const DetailsTab: React.FC<DetailsTabProps> = ({ formData, updateFormData }) => {
  const [eventRegistrationForms, setEventRegistrationForms] = useState<EventRegistrationForm[]>([]);
  const [promoVideoError, setPromoVideoError] = useState<string>('');

  useEffect(() => {
    fetchEventRegistrationForms();
  }, []);

  const fetchEventRegistrationForms = async () => {
    try {
      const { data, error } = await supabase
        .from('event_registration_forms')
        .select('id, name')
        .order('name');

      if (error) throw error;
      setEventRegistrationForms(data || []);
    } catch (error) {
      console.error('Error fetching event registration forms:', error);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    console.log(`DetailsTab - Updating field ${field} with value:`, value);
    updateFormData({ [field]: value });
  };

  const validateYouTubeUrl = (url: string): boolean => {
    if (!url || url.trim() === '') return true; // Empty is valid
    return url.includes('youtube.com') || url.includes('youtu.be');
  };

  const handlePromoVideoUrlChange = (value: string) => {
    if (validateYouTubeUrl(value)) {
      setPromoVideoError('');
      handleInputChange('promo_video_url', value);
    } else {
      setPromoVideoError('Please enter a valid YouTube URL (youtube.com or youtu.be)');
    }
  };

  const handleGalleryImagesChange = (images: string[]) => {
    updateFormData({ gallery_images: images });
  };

  const handleBackgroundImageChange = (url: string) => {
    updateFormData({ background_image_url: url });
  };

  // Debug logging for rules field
  console.log('DetailsTab - Current rules value:', formData.rules);
  console.log('DetailsTab - Full formData:', formData);

  return (
    <div className="space-y-6">
      <div className="text-sm text-gray-600 mb-4">
        The following fields are used for the front-end display of your event.
      </div>

      {/* Display Settings Section */}
      <Card>
        <CardHeader>
          <CardTitle>Display Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="event_format">Event Format</Label>
            <BasicRichTextEditor
              value={formData.event_format || ''}
              onChange={(value) => handleInputChange('event_format', value)}
              placeholder="Describe the event format..."
            />
          </div>
          
          <div>
            <Label htmlFor="checkin_instructions">Check-in Instructions</Label>
            <BasicRichTextEditor
              value={formData.checkin_instructions || ''}
              onChange={(value) => handleInputChange('checkin_instructions', value)}
              placeholder="Provide check-in instructions..."
            />
          </div>
          
          <div>
            <Label htmlFor="rules">Rules</Label>
            <BasicRichTextEditor
              value={formData.rules || ''}
              onChange={(value) => {
                console.log('Rules field updated with value:', value);
                console.log('Rules field length:', value?.length);
                handleInputChange('rules', value);
                // Force immediate update to check if the formData is being updated
                setTimeout(() => {
                  console.log('FormData after rules update:', formData.rules);
                }, 100);
              }}
              placeholder="Enter event rules..."
            />
          </div>
          
          <div>
            <Label htmlFor="pricing_notes">Pricing Notes</Label>
            <BasicRichTextEditor
              value={formData.pricing_notes || ''}
              onChange={(value) => handleInputChange('pricing_notes', value)}
              placeholder="Add pricing notes..."
            />
          </div>

          <div>
            <Label htmlFor="age_groups_description">Age Groups</Label>
            <BasicRichTextEditor
              value={formData.age_groups_description || ''}
              onChange={(value) => handleInputChange('age_groups_description', value)}
              placeholder="Describe the age groups for this event..."
            />
          </div>

          {/* Background Image Upload */}
          <BackgroundImageUpload
            eventId={formData.id}
            currentImageUrl={formData.background_image_url || ''}
            onImageChange={handleBackgroundImageChange}
          />
        </CardContent>
      </Card>

      {/* Media & Extras Section */}
      <Card>
        <CardHeader>
          <CardTitle>Media & Extras</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="hotel_booking_url">Hotel Booking URL</Label>
            <Input
              id="hotel_booking_url"
              type="url"
              value={formData.hotel_booking_url || ''}
              onChange={(e) => handleInputChange('hotel_booking_url', e.target.value)}
            />
          </div>
          
          <div>
            <Label htmlFor="promo_video_url">YouTube Video URL</Label>
            <Input
              id="promo_video_url"
              type="url"
              value={formData.promo_video_url || ''}
              onChange={(e) => handlePromoVideoUrlChange(e.target.value)}
              placeholder="Paste YouTube video link here (e.g., https://www.youtube.com/watch?v=xxxxx)"
              className={promoVideoError ? 'border-red-500' : ''}
            />
            {promoVideoError && (
              <p className="text-sm text-red-500 mt-1">{promoVideoError}</p>
            )}
          </div>

          <div>
            <Label htmlFor="live_streaming_description">Live Streaming Description</Label>
            <BasicRichTextEditor
              value={formData.live_streaming_description || ''}
              onChange={(value) => handleInputChange('live_streaming_description', value)}
              placeholder="Describe the live streaming details and access information..."
            />
          </div>

          <div>
            <Label htmlFor="live_streaming_url">Live Streaming URL</Label>
            <Input
              id="live_streaming_url"
              type="url"
              value={formData.live_streaming_url || ''}
              onChange={(e) => handleInputChange('live_streaming_url', e.target.value)}
              placeholder="https://example.com/live-stream"
            />
          </div>

          {/* Gallery Images Section */}
          <EventGalleryUpload
            galleryImages={formData.gallery_images || []}
            onChange={handleGalleryImagesChange}
          />

          <div className="flex items-center space-x-2">
            <Checkbox
              id="enable_media_credentials"
              checked={formData.enable_media_credentials || false}
              onCheckedChange={(checked) => handleInputChange('enable_media_credentials', checked)}
            />
            <Label htmlFor="enable_media_credentials">Enable Media Credentials</Label>
          </div>

          {formData.enable_media_credentials && (
            <div className="space-y-4 pl-6 border-l-2 border-gray-200">
              <div>
                <Label htmlFor="media_credentials_description">Media Credentials Description</Label>
                <BasicRichTextEditor
                  value={formData.media_credentials_description || ''}
                  onChange={(value) => handleInputChange('media_credentials_description', value)}
                  placeholder="Describe the media credentials process and requirements..."
                />
              </div>
              
              <div>
                <Label htmlFor="media_credentials_form">Media Credentials Form</Label>
                <Select 
                  value={formData.media_credentials_form_id || ''} 
                  onValueChange={(value) => handleInputChange('media_credentials_form_id', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select form for media credentials" />
                  </SelectTrigger>
                  <SelectContent>
                    {eventRegistrationForms.map((form) => (
                      <SelectItem key={form.id} value={form.id}>
                        {form.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="media_credentials_notification_email">Notification Email</Label>
                <Input
                  id="media_credentials_notification_email"
                  type="email"
                  value={formData.media_credentials_notification_email || ''}
                  onChange={(e) => handleInputChange('media_credentials_notification_email', e.target.value)}
                  placeholder="admin@example.com"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Email address to notify when media credentials are requested
                </p>
              </div>
              
              <div>
                <Label htmlFor="media_credentials_price">Media Credentials Price</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="media_credentials_price"
                    type="number"
                    min="0"
                    step="0.01"
                    className="pl-7"
                    value={formData.media_credentials_price ? (formData.media_credentials_price / 100).toFixed(2) : '0'}
                    onChange={(e) => handleInputChange('media_credentials_price', Math.round(parseFloat(e.target.value || '0') * 100))}
                    placeholder="0.00"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Leave at $0 for free media credentials
                </p>
              </div>
              
              <div>
                <Label htmlFor="media_credentials_coupon_code">Coupon Code (optional)</Label>
                <Input
                  id="media_credentials_coupon_code"
                  type="text"
                  value={formData.media_credentials_coupon_code || ''}
                  onChange={(e) => handleInputChange('media_credentials_coupon_code', e.target.value.toUpperCase())}
                  placeholder="e.g., MEDIA2024"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Optional coupon code for free/discounted media credentials
                </p>
              </div>
              
              {formData.media_credentials_coupon_code && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="media_credentials_coupon_discount_type">Discount Type</Label>
                    <select
                      id="media_credentials_coupon_discount_type"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      value={formData.media_credentials_coupon_discount_type || 'percentage'}
                      onChange={(e) => handleInputChange('media_credentials_coupon_discount_type', e.target.value)}
                    >
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed">Fixed Amount ($)</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="media_credentials_coupon_discount_value">
                      Discount Value {formData.media_credentials_coupon_discount_type === 'fixed' ? '($)' : '(%)'}
                    </Label>
                    <Input
                      id="media_credentials_coupon_discount_value"
                      type="number"
                      min="0"
                      max={formData.media_credentials_coupon_discount_type === 'percentage' ? 100 : undefined}
                      step={formData.media_credentials_coupon_discount_type === 'fixed' ? '0.01' : '1'}
                      value={formData.media_credentials_coupon_discount_value || (formData.media_credentials_coupon_discount_type === 'percentage' ? 100 : 0)}
                      onChange={(e) => handleInputChange('media_credentials_coupon_discount_value', parseFloat(e.target.value || '0'))}
                      placeholder={formData.media_credentials_coupon_discount_type === 'percentage' ? '100' : '0.00'}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {formData.media_credentials_coupon_discount_type === 'percentage' 
                        ? 'Enter 100 for free, 50 for half off, etc.' 
                        : 'Enter dollar amount to discount'}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* College Coaches Request Form Section */}
      <Card>
        <CardHeader>
          <CardTitle>College Coaches Request Form</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="enable_coach_info"
              checked={formData.enable_coach_info || false}
              onCheckedChange={(checked) => handleInputChange('enable_coach_info', checked)}
            />
            <Label htmlFor="enable_coach_info">Enable College Coaches Request Form</Label>
          </div>

          {formData.enable_coach_info && (
            <div className="space-y-4 pl-6 border-l-2 border-gray-200">
              <div>
                <Label htmlFor="coach_info_description">College Coaches Description</Label>
                <BasicRichTextEditor
                  value={formData.coach_info_description || ''}
                  onChange={(value) => handleInputChange('coach_info_description', value)}
                  placeholder="Describe the college coach request process and requirements..."
                />
              </div>
              
              <div>
                <Label htmlFor="coach_info_form">College Coaches Form</Label>
                <Select 
                  value={formData.coach_info_form_id || ''} 
                  onValueChange={(value) => handleInputChange('coach_info_form_id', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select form for college coaches" />
                  </SelectTrigger>
                  <SelectContent>
                    {eventRegistrationForms.map((form) => (
                      <SelectItem key={form.id} value={form.id}>
                        {form.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notification Settings Section */}
      <Card>
        <CardHeader>
          <CardTitle>Notification Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="coach_notification_email">Coach Registration Notifications</Label>
            <Input
              id="coach_notification_email"
              type="text"
              value={formData.coach_notification_email || ''}
              onChange={(e) => handleInputChange('coach_notification_email', e.target.value)}
              placeholder="admin@example.com, manager@example.com"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Email addresses to notify when coaches register for this event (comma-separated for multiple)
            </p>
          </div>
        </CardContent>
      </Card>

    </div>
  );
};

export default DetailsTab;